import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, MessageSquare, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BRAND } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="p-6 rounded-2xl bg-white border border-[#E5DDC9]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-[#64748B]">{label}</span>
        <Icon className="w-5 h-5 text-[#AC9667]" />
      </div>
      <p className="font-serif text-4xl text-[#005165]">{value}</p>
    </div>
  );
}

function SubmissionList({ items, render, testidPrefix }) {
  if (!items?.length)
    return <p className="text-sm text-[#64748B] py-8 text-center">No submissions yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div
          key={it.id || i}
          data-testid={`${testidPrefix}-${i}`}
          className="bg-white rounded-xl p-5 border border-[#E5DDC9]/70"
        >
          {render(it)}
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { "X-Admin-Password": password },
      });
      setData(res.data);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAuthed(false);
    setPassword("");
    setData(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center px-6" data-testid="admin-login">
        <form
          onSubmit={login}
          className="w-full max-w-md bg-white rounded-3xl p-8 md:p-10 border border-[#E5DDC9] shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
        >
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#005165] items-center justify-center mb-5">
            <Lock className="w-6 h-6 text-[#C4B088]" />
          </div>
          <h1 className="font-serif text-3xl text-[#005165] mb-1">Admin Sign In</h1>
          <p className="text-sm text-[#64748B] mb-6">{BRAND.name}</p>
          <Label htmlFor="pw">Password</Label>
          <Input id="pw" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
          <Button data-testid="admin-submit" type="submit" disabled={loading} className="mt-5 w-full bg-[#005165] text-[#FFFFFF] rounded-full h-12 btn-shimmer">
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  const c = data?.counts || { intakes: 0, contacts: 0, applications: 0 };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-4xl text-[#005165]">Submissions</h1>
            <p className="text-sm text-[#64748B] mt-1">{BRAND.name}</p>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full border-[#005165]/20" data-testid="admin-logout">
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Intake Forms" value={c.intakes} />
          <StatCard icon={MessageSquare} label="Contact Messages" value={c.contacts} />
          <StatCard icon={Briefcase} label="Caregiver Applications" value={c.applications} />
        </div>

        <Tabs defaultValue="intakes">
          <TabsList className="bg-white border border-[#E5DDC9]">
            <TabsTrigger value="intakes" data-testid="tab-intakes">Intakes</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="intakes" className="mt-6">
            <SubmissionList
              items={data?.intakes}
              testidPrefix="intake-item"
              render={(it) => (
                <>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#005165]">{it.full_name}</span>
                    <span className="text-[#64748B]">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#4B5563]">{it.email} · {it.phone} · {it.relationship_to_care_recipient}</p>
                  <p className="text-sm text-[#4B5563] mt-1">For: {it.care_recipient_name || "—"} ({it.care_recipient_age || "—"})</p>
                  <p className="text-xs text-[#64748B] mt-2">Services: {(it.services_needed || []).join(", ") || "—"}</p>
                  {it.additional_notes && (
                    <p className="text-sm text-[#1F2937] mt-2 italic">"{it.additional_notes}"</p>
                  )}
                </>
              )}
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <SubmissionList
              items={data?.contacts}
              testidPrefix="contact-item"
              render={(it) => (
                <>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#005165]">{it.full_name}</span>
                    <span className="text-[#64748B]">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#4B5563]">{it.email} · {it.phone || "—"}</p>
                  {it.subject && <p className="text-sm text-[#005165] mt-1 font-medium">{it.subject}</p>}
                  <p className="text-sm text-[#1F2937] mt-2">{it.message}</p>
                </>
              )}
            />
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <SubmissionList
              items={data?.applications}
              testidPrefix="application-item"
              render={(it) => (
                <>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-[#005165]">{it.full_name} · {it.position}</span>
                    <span className="text-[#64748B]">{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#4B5563]">{it.email} · {it.phone}</p>
                  <p className="text-xs text-[#64748B] mt-2">Experience: {it.years_experience || "—"} · Certs: {it.certifications || "—"}</p>
                  <p className="text-xs text-[#64748B]">Availability: {it.availability || "—"}</p>
                  {it.why_join && <p className="text-sm text-[#1F2937] mt-2 italic">"{it.why_join}"</p>}
                </>
              )}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
